"use client";

import { createStore } from "zustand/vanilla";
import type { BulkImportResult } from "@/types/bulk-import";

type UploadFieldState = {
  url: string;
  uploading: boolean;
  error: string;
  setUrl: (url: string) => void;
  setUploading: (uploading: boolean) => void;
  setError: (error: string) => void;
};

export function createUploadFieldStore(defaultValue = "") {
  return createStore<UploadFieldState>((set) => ({
    url: defaultValue,
    uploading: false,
    error: "",
    setUrl: (url) => set({ url }),
    setUploading: (uploading) => set({ uploading }),
    setError: (error) => set({ error })
  }));
}

type BulkUploadState = {
  dragActive: boolean;
  validation: BulkImportResult | null;
  importResult: BulkImportResult | null;
  downloadError: string;
  setDragActive: (dragActive: boolean) => void;
  setValidation: (validation: BulkImportResult | null) => void;
  setImportResult: (importResult: BulkImportResult | null) => void;
  setDownloadError: (downloadError: string) => void;
};

export function createBulkUploadStore() {
  return createStore<BulkUploadState>((set) => ({
    dragActive: false,
    validation: null,
    importResult: null,
    downloadError: "",
    setDragActive: (dragActive) => set({ dragActive }),
    setValidation: (validation) => set({ validation }),
    setImportResult: (importResult) => set({ importResult }),
    setDownloadError: (downloadError) => set({ downloadError })
  }));
}

type FormSubmitState = {
  isSubmitting: boolean;
  error: string | null;
  success: string | null;
  setIsSubmitting: (isSubmitting: boolean) => void;
  setError: (error: string | null) => void;
  setSuccess: (success: string | null) => void;
};

export function createFormSubmitStore() {
  return createStore<FormSubmitState>((set) => ({
    isSubmitting: false,
    error: null,
    success: null,
    setIsSubmitting: (isSubmitting) => set({ isSubmitting }),
    setError: (error) => set({ error }),
    setSuccess: (success) => set({ success })
  }));
}

export function createMagazineStore<TDocument>(initialPage: number) {
  return createStore<{
    document: TDocument | null;
    error: string;
    zoom: number;
    pageInput: string;
    setDocument: (document: TDocument | null) => void;
    setError: (error: string) => void;
    setZoom: (value: number | ((current: number) => number)) => void;
    setPageInput: (pageInput: string) => void;
  }>((set) => ({
    document: null,
    error: "",
    zoom: 1.1,
    pageInput: String(initialPage),
    setDocument: (document) => set({ document }),
    setError: (error) => set({ error }),
    setZoom: (value) => set((state) => ({
      zoom: typeof value === "function" ? value(state.zoom) : value
    })),
    setPageInput: (pageInput) => set({ pageInput })
  }));
}

export function createBusinessListingStore() {
  return createStore<{
    step: number;
    setStep: (value: number | ((current: number) => number)) => void;
  }>((set) => ({
    step: 0,
    setStep: (value) => set((state) => ({
      step: typeof value === "function" ? value(state.step) : value
    }))
  }));
}

export function createCuratedItemStore(
  entityType: "business" | "event" | "deal",
  pageId: string
) {
  return createStore<{
    entityType: "business" | "event" | "deal";
    pageId: string;
    setEntityType: (entityType: "business" | "event" | "deal") => void;
    setPageId: (pageId: string) => void;
  }>((set) => ({
    entityType,
    pageId,
    setEntityType: (nextEntityType) => set({ entityType: nextEntityType }),
    setPageId: (nextPageId) => set({ pageId: nextPageId })
  }));
}
